import React from "react";
import { DateTime } from "luxon";
import { formatCurrency } from "../../utils/currency";
import { formatZoneIndicator } from "../../utils/timezoneLabel";

/**
 * CheckoutForm - Step 3 of ticket creation wizard
 * Final review and publishing options.
 * Payment method is preset to Stripe for MVP — multiple payment methods is future roadmap.
 */
const CheckoutForm = ({ formData, updateFormSection, onPublish, onSaveDraft, isLoading, setError, editMode = false, orgCurrency = null, orgTimezone = null, payoutsReady = null }) => {
    const checkout = formData.checkout || {};

    const handleTermsChange = (value) => {
        updateFormSection('checkout', {
            ...checkout,
            termsAndConditions: value
        });
    };

    // Check if form is ready for publishing
    const isReadyToPublish = () => {
        const { eventDetails, ticketSetup, checkout } = formData;

        const ready = (
            eventDetails?.title &&
            eventDetails?.startDateTime &&
            eventDetails?.endDateTime &&
            eventDetails?.location?.description &&
            ticketSetup?.ticketTypes?.length > 0 &&
            ticketSetup.ticketTypes.every(ticket => ticket.name && ticket.price !== undefined && ticket.quantity) &&
            checkout?.termsAndConditions?.trim()
        );


        return ready;
    };

    // The wizard holds times as a local "YYYY-MM-DDTHH:MM" wall-clock string in the EVENT's
    // timezone (UTC conversion happens at submit). Render that wall-clock as-is, then label it
    // with the canonical indicator for the EVENT's tz (not the org tz — those differ when the
    // event is held in a different city), matching the landing/purchase/mobile/email format.
    const eventTz = formData.eventDetails?.timezone || orgTimezone;
    const formatDateTime = (dateTimeString) => {
        if (!dateTimeString) return 'Not set';
        const dt = DateTime.fromISO(dateTimeString);
        if (!dt.isValid) return 'Not set';
        const formatted = dt.toFormat('cccc, dd LLLL yyyy, h:mm a');
        if (eventTz) {
            // Interpret the wall-clock in the event tz so the offset is DST-correct for that date.
            const zoned = DateTime.fromISO(dateTimeString, { zone: eventTz });
            return `${formatted} (${formatZoneIndicator(eventTz, zoned)})`;
        }
        return formatted;
    };

    const formatPrice = (price, currency = 'AUD') => {
        if (price === 0) return 'Free';
        return formatCurrency(price, currency);
    };

    return (
        <div className="checkout-form">
            <h2>Checkout & Publishing</h2>
            <p className="form-description">
                Review your ticket details and configure payment options before publishing.
            </p>

            {/* Event Summary */}
            <div className="form-section">
                <h3>Event Summary</h3>
                <div className="event-summary-card">
                    <div className="summary-row">
                        <span className="label">Event Title:</span>
                        <span className="value">{formData.eventDetails?.title || 'Not set'}</span>
                    </div>
                    <div className="summary-row">
                        <span className="label">Start:</span>
                        <span className="value">{formatDateTime(formData.eventDetails?.startDateTime)}</span>
                    </div>
                    <div className="summary-row">
                        <span className="label">End:</span>
                        <span className="value">{formatDateTime(formData.eventDetails?.endDateTime)}</span>
                    </div>
                    <div className="summary-row">
                        <span className="label">Location:</span>
                        <span className="value">{formData.eventDetails?.location?.description || 'Not set'}</span>
                    </div>
                    {formData.eventDetails?.importSource === 'ics' && (
                        <div className="summary-row">
                            <span className="label">Source:</span>
                            <span className="value">Imported from {formData.eventDetails.icsData?.filename}</span>
                        </div>
                    )}
                </div>
            </div>

            {/* Ticket Types Summary */}
            <div className="form-section">
                <h3>Ticket Types Summary</h3>
                {formData.ticketSetup?.ticketTypes?.length > 0 ? (
                    <div className="tickets-summary-table">
                        <div className="summary-header">
                            <span>Type</span>
                            <span>Price</span>
                            <span>Quantity</span>
                            <span>Total Value</span>
                        </div>
                        {formData.ticketSetup.ticketTypes.map((ticket, index) => (
                            <div key={ticket.id || index} className="summary-row">
                                <div>
                                    <strong>{ticket.name}</strong>
                                    {ticket.description && <div className="ticket-desc">{ticket.description}</div>}
                                    {ticket.restrictions && <div className="ticket-restrictions">Restrictions: {ticket.restrictions}</div>}
                                </div>
                                <span>{formatPrice(ticket.price, ticket.currency)}</span>
                                <span>{ticket.quantity}</span>
                                <span>{formatPrice((ticket.price || 0) * (ticket.quantity || 0), ticket.currency)}</span>
                            </div>
                        ))}
                        <div className="summary-total">
                            <span><strong>Total Available Tickets:</strong></span>
                            <span></span>
                            <span><strong>{formData.ticketSetup.ticketTypes.reduce((sum, ticket) => sum + (ticket.quantity || 0), 0)}</strong></span>
                            <span><strong>
                                Max Revenue: {formatCurrency(
                                    formData.ticketSetup.ticketTypes.reduce((sum, ticket) =>
                                        sum + ((ticket.price || 0) * (ticket.quantity || 0)), 0
                                    ),
                                    orgCurrency || formData.ticketSetup.ticketTypes[0]?.currency
                                )}
                            </strong></span>
                        </div>
                    </div>
                ) : (
                    <p className="no-tickets">No ticket types configured</p>
                )}
            </div>

            {/* Payment Methods */}
            <div className="form-section">
                <h3>Payment Method</h3>
                <p>Tickets are processed securely via <strong>Stripe</strong> (credit/debit cards).</p>
            </div>

            {/* Terms and Conditions */}
            <div className="form-section">
                <h3>Terms and Conditions *</h3>
                <div className="form-row">
                    <div className="form-group full-width">
                        <label htmlFor="terms-conditions">
                            Event Terms & Conditions *
                        </label>
                        <textarea
                            id="terms-conditions"
                            value={checkout.termsAndConditions || ""}
                            onChange={(e) => handleTermsChange(e.target.value)}
                            placeholder="Enter terms and conditions for this event. For example: Age restrictions, dress code, prohibited items, etc."
                            className="form-textarea"
                            rows={4}
                        />
                        <small className="form-help">
                            Required. These terms will be shown to customers during checkout and must be accepted before purchase.
                        </small>
                    </div>
                </div>
            </div>

            {/* Sales Period Summary */}
            {(formData.ticketSetup?.salesPeriod?.startDate || formData.ticketSetup?.salesPeriod?.endDate) && (
                <div className="form-section">
                    <h3>Sales Period</h3>
                    <div className="sales-period-summary">
                        {formData.ticketSetup.salesPeriod.startDate ? (
                            <div className="summary-row">
                                <span className="label">Sales Start:</span>
                                <span className="value">{formatDateTime(formData.ticketSetup.salesPeriod.startDate)}</span>
                            </div>
                        ) : (
                            <div className="summary-row">
                                <span className="label">Sales Start:</span>
                                <span className="value">Immediately after publishing</span>
                            </div>
                        )}
                        {formData.ticketSetup.salesPeriod.endDate ? (
                            <div className="summary-row">
                                <span className="label">Sales End:</span>
                                <span className="value">{formatDateTime(formData.ticketSetup.salesPeriod.endDate)}</span>
                            </div>
                        ) : (
                            <div className="summary-row">
                                <span className="label">Sales End:</span>
                                <span className="value">When event starts</span>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Pre-publish Validation */}
            <div className="form-section">
                <h3>Publishing Checklist</h3>
                <div className="publishing-checklist">
                    <div className={`checklist-item ${formData.eventDetails?.title ? 'valid' : 'invalid'}`}>
                        <span className="check-icon">{formData.eventDetails?.title ? '✓' : '✗'}</span>
                        Event title set
                    </div>
                    <div className={`checklist-item ${formData.eventDetails?.startDateTime ? 'valid' : 'invalid'}`}>
                        <span className="check-icon">{formData.eventDetails?.startDateTime ? '✓' : '✗'}</span>
                        Event start date/time set
                    </div>
                    <div className={`checklist-item ${formData.eventDetails?.endDateTime ? 'valid' : 'invalid'}`}>
                        <span className="check-icon">{formData.eventDetails?.endDateTime ? '✓' : '✗'}</span>
                        Event end date/time set
                    </div>
                    <div className={`checklist-item ${formData.eventDetails?.location?.description ? 'valid' : 'invalid'}`}>
                        <span className="check-icon">{formData.eventDetails?.location?.description ? '✓' : '✗'}</span>
                        Event location set
                    </div>
                    <div className={`checklist-item ${(formData.ticketSetup?.ticketTypes?.length > 0) ? 'valid' : 'invalid'}`}>
                        <span className="check-icon">{(formData.ticketSetup?.ticketTypes?.length > 0) ? '✓' : '✗'}</span>
                        At least one ticket type configured
                    </div>
                    {payoutsReady === false ? (
                        <div className="checklist-item invalid">
                            <span className="check-icon">✗</span>
                            Payout account connected
                        </div>
                    ) : (
                        <div className="checklist-item valid">
                            <span className="check-icon">✓</span>
                            Payment via Stripe
                        </div>
                    )}
                    <div className={`checklist-item ${checkout.termsAndConditions ? 'valid' : 'invalid'}`}>
                        <span className="check-icon">{checkout.termsAndConditions ? '✓' : '✗'}</span>
                        Terms and conditions provided
                    </div>
                </div>
            </div>

            {/* Action Buttons */}
            <div className="form-section">
                <div className="publish-section">
                    {/* Save Draft Button - Hidden in edit mode (published events update directly) */}
                    {!editMode && (
                        <div className="draft-actions">
                            <button
                                type="button"
                                onClick={onSaveDraft}
                                className="btn-secondary"
                                disabled={isLoading}>
                                {isLoading ? "Saving..." : "Save Draft"}
                            </button>
                        </div>
                    )}

                    {/* Publish Button */}
                    {payoutsReady === false ? (
                        <div>
                            <button
                                type="button"
                                className="publish-btn disabled"
                                disabled>
                                Connect Payout Account to Publish
                            </button>
                            <p className="publish-note error">
                                Ticket buyers pay by card, so a payout account must be connected before this event
                                can go live. Set one up in <a href="/manage-finances">Manage Finances</a>, then come
                                back to publish. Your draft is saved in the meantime.
                            </p>
                        </div>
                    ) : isReadyToPublish() ? (
                        <div>
                            <button
                                type="button"
                                onClick={onPublish}
                                className="publish-btn"
>
                                {isLoading ? (editMode ? "Updating..." : "Publishing...") : (editMode ? "Update Event" : "Publish Tickets")}
                            </button>
                            <p className="publish-note">
                                {editMode
                                    ? "Changes will be applied immediately to the published event."
                                    : "Once published, your tickets will be live and available for purchase. You can still make changes through your dashboard."
                                }
                            </p>
                        </div>
                    ) : (
                        <div>
                            <button
                                type="button"
                                className="publish-btn disabled"
                                disabled>
                                Complete Required Fields to Publish
                            </button>
                            <p className="publish-note error">
                                Please complete all required fields in the checklist above before publishing.
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CheckoutForm;